import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import { UserType } from "../types/usersTypes";
import { AsyncThunkConfig, RootState } from "./store";
import { usersAPI } from "../app/api";
import { AppStatusType } from "./appStatusReducer";
import { message } from "antd";

interface InitialStateType {
    users: UserType[]
    defaultUsers: UserType[]
    currentPage: number
    isUsersLoading: boolean
}

const initialState: InitialStateType = {
    users: [],
    defaultUsers: [],
    currentPage: 0,
    isUsersLoading: false
}

export const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        onSetUsers: (state, action: PayloadAction<UserType[]>) => {
            state.users = action.payload
            state.defaultUsers = action.payload
        },
        onSetGridRows: (state, action: PayloadAction<UserType[]>) => {
            state.users = action.payload
        },
        onSetCurrentPage: (state, action: PayloadAction<number>) => {
            state.currentPage = action.payload
        },
        onSetIsUsersLoading: (state, action: PayloadAction<boolean>) => {
            state.isUsersLoading = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(GetUsersList.fulfilled, (state, action) => {
                state.users = action.payload
                state.defaultUsers = action.payload
            })
            .addCase(UpdateUserStatus.pending, (state, action) => {
                const { id, isActive } = action.meta.arg
                const userInList = state.users.find(u => u.id === id)
                if (userInList) {
                  userInList.is_active = isActive
                }
                const userInDefaultList = state.defaultUsers.find(u => u.id === id)
                if (userInDefaultList) userInDefaultList.is_active = isActive
              })
            .addCase(UpdateUserStatus.rejected, (state, action) => {
                const { id, isActive } = action.meta.arg
                const userInList = state.users.find(u => u.id === id)
                if (userInList) {
                  userInList.is_active = !isActive
                }
                const userInDefaultList = state.defaultUsers.find(u => u.id === id)
                if (userInDefaultList) userInDefaultList.is_active = !isActive
              })
            .addCase(RemoveUser.fulfilled, (state, action) => {
                state.users = state.users.filter((user) => user.id !== action.payload)
                state.defaultUsers = state.defaultUsers.filter((user) => user.id !== action.payload)
            })
    }
})

export const {
    onSetUsers,
    onSetGridRows,
    onSetCurrentPage,
    onSetIsUsersLoading
} = usersSlice.actions

export const selectUsersGridRows = (state: RootState): UserType[] => state.users.users
export const selectDefaultUsersGridRows = (state: RootState): UserType[] => state.users.defaultUsers
export const selectCurrentPage = (state: RootState): number => state.users.currentPage
export const selectIsUsersLoading = (state: RootState): boolean => state.users.isUsersLoading

export const GetUsersList = createAsyncThunk<UserType[], void, AsyncThunkConfig>(
    'users/getUsersList',
    async (_, thunkAPI) => {
        thunkAPI.dispatch(onSetIsUsersLoading(true))
        try {
            const {status, data} = await usersAPI.getUsers()
            thunkAPI.dispatch(onSetIsUsersLoading(false))
            if (status === 200 && data) {
                return thunkAPI.fulfillWithValue(data.users, {appStatus: AppStatusType.idle})
            } else {
                return thunkAPI.rejectWithValue(data.users)
            }
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error?.response?.data?.message)
        }
    }
)

export const UpdateUserStatus = createAsyncThunk<UserType, {id: number, isActive: boolean}, AsyncThunkConfig>(
    'users/updateUserStatus',
    async ({id, isActive}, thunkAPI) => {
        try {
            const {status, data} = await usersAPI.updateUserStatus(id, isActive)
            if (status === 200 && data) {
                return thunkAPI.fulfillWithValue(data, {appStatus: AppStatusType.idle})
            } else {
                return thunkAPI.rejectWithValue(data)
            }
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error?.response?.data?.message)
        }
    }
)

export const RemoveUser = createAsyncThunk<number, number, AsyncThunkConfig>(
    'users/removeUser',
    async (userId, thunkAPI) => {
        try {
            const {status} = await usersAPI.removeUser(userId)
            if (status === 200) {
                message.success('User was successfully deleted')
                return thunkAPI.fulfillWithValue(userId, {appStatus: AppStatusType.idle})
            } else {
                message.error('Failed to delete user')
                return thunkAPI.rejectWithValue(userId)
            }
        } catch (error: any) {
            message.error(error?.response?.data?.message)
            return thunkAPI.rejectWithValue(error?.response?.data?.message)
        }
    }
)

export default usersSlice.reducer
